import JSZip from "jszip";

/**
 * Reescribe la hoja "Comunas" de la plantilla de pedidos con la cobertura real
 * del operador que atiende al comercio, y reapunta el desplegable a ese rango.
 *
 * Por qué se PARCHEA el binario en vez de generar el libro con SheetJS: la
 * versión community no escribe validaciones de datos. Generar la plantilla
 * costaría los dos desplegables que hoy tiene ("Tipo de encomienda" y "Comuna")
 * — es la razón por la que `plantillaAltas.js` publica su cobertura como hoja
 * consultable en vez de como lista. Acá no hace falta ese sacrificio: el
 * desplegable de comuna ya existe en el archivo y apunta a un RANGO,
 *
 *     <x14:dataValidation type="list">
 *       <x14:formula1><xm:f>Comunas!$A$1:$A$37</xm:f></x14:formula1>
 *       <xm:sqref>G3:G100</xm:sqref>
 *
 * así que alcanza con reemplazar el contenido de esa hoja y mover el `37`.
 *
 * Mapeo de archivos dentro del .xlsx (verificado sobre ambas plantillas):
 * "Comunas" es la relación rId4 → `xl/worksheets/sheet4.xml`, hoja oculta.
 *
 * Las celdas nuevas se escriben con `t="inlineStr"` para no tener que tocar
 * `sharedStrings.xml` (que es donde el original guarda estos textos).
 */

const COMUNAS_SHEET_PATH = "xl/worksheets/sheet4.xml";
const MAIN_SHEET_PATH = "xl/worksheets/sheet1.xml";

/** Estilo de las celdas de la hoja original, para no romper su apariencia. */
const CELL_STYLE = "44";

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Hoja "Comunas" completa, con una comuna por fila en la columna A. */
function buildComunasSheet(communes) {
  const rows = communes
    .map(
      (name, i) =>
        `<row r="${i + 1}" spans="1:1"><c r="A${i + 1}" s="${CELL_STYLE}" t="inlineStr"><is><t>${escapeXml(name)}</t></is></c></row>`,
    )
    .join("");

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<dimension ref="A1:A${communes.length}"/>` +
    "<sheetViews><sheetView workbookViewId=\"0\"/></sheetViews>" +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    `<sheetData>${rows}</sheetData>` +
    '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.511811023622047" footer="0.511811023622047"/>' +
    "</worksheet>"
  );
}

/**
 * Descarga el .xlsx base y devuelve una copia con la cobertura del operador.
 *
 * @param {string} templateUrl URL del asset (el import de CRA devuelve una URL).
 * @param {string[]} communes  Nombres de comuna, ya deduplicados y ordenados.
 * @returns {Promise<Blob>}
 * @throws si el archivo no tiene la forma esperada — el llamador debe caer a la
 *   plantilla estática en vez de entregar un archivo a medio parchear.
 */
export async function buildTemplateWithCoverage(templateUrl, communes) {
  if (!Array.isArray(communes) || communes.length === 0) {
    throw new Error("Sin comunas de cobertura");
  }

  const response = await fetch(templateUrl);
  if (!response.ok) throw new Error(`No se pudo leer la plantilla (${response.status})`);

  const zip = await JSZip.loadAsync(await response.arrayBuffer());

  const comunasSheet = zip.file(COMUNAS_SHEET_PATH);
  const mainSheet = zip.file(MAIN_SHEET_PATH);
  if (!comunasSheet || !mainSheet) {
    throw new Error("La plantilla no tiene la estructura esperada");
  }

  // 1) La hoja de comunas, entera.
  zip.file(COMUNAS_SHEET_PATH, buildComunasSheet(communes));

  // 2) El rango del desplegable. Se reemplaza sólo la referencia a la hoja
  //    Comunas: si el archivo cambiara y esa fórmula no estuviera, se aborta en
  //    vez de entregar un desplegable apuntando a filas que ya no existen.
  const mainXml = await mainSheet.async("string");
  // Sin `test()` sobre una regex /g a propósito: ese método mueve `lastIndex` y
  // deja la siguiente operación empezando desde el medio de la cadena.
  if (!mainXml.includes("Comunas!$A$1:$A$")) {
    throw new Error("No se encontró el rango del desplegable de comunas");
  }
  zip.file(
    MAIN_SHEET_PATH,
    mainXml.replace(/(Comunas!\$A\$1:\$A\$)\d+/g, `$1${communes.length}`),
  );

  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
